/**
 * Comportamento das seções portadas do Magento (vv-m-*), com a mesma lógica dos scripts inline de
 * home.phtml, product-shelf.phtml e viavini-product-page.phtml:
 *  - prateleiras: setas andam 2 cards, pontos acompanham a rolagem, setas desativadas nas pontas;
 *    no celular (até 767px, layout de 05/10/2026) a vitrine anda por grupos de dois cards inteiros,
 *    com uma bolinha por grupo, cards fora do grupo visível inertes e sem autoplay;
 *  - carrosséis paginados (produtores, harmonizações, Instagram): páginas e pontos calculados;
 *  - curadoria: pontos acompanham a rolagem (scroll-snap);
 *  - arrastar com o mouse em todos os carrosséis (sem quebrar o clique);
 *  - hero: rotação com crossfade e pontos (o rotador de fallback da produção);
 *  - página do produto: quantidade +/-, faixas do desconto progressivo, "ver mais", acordeões do
 *    celular, barra fixa (preço + Adicionar, aciona o botão principal) e cronômetro de campanha;
 *  - compra: um envio por vez por formulário (sem adição dupla) e "Adicionado!" só sem erro;
 *  - <vv-recommendations>: carrega "Você também pode gostar" pela API de recomendações.
 * Tudo é idempotente e reinicializa no editor de temas (shopify:section:load).
 */
(function () {
  'use strict';

  if (window.VvMagento) return;

  function once(el, key) {
    if (el.dataset[key] === '1') return false;
    el.dataset[key] = '1';
    return true;
  }

  function gapOf(el, fallback) {
    var cs = getComputedStyle(el);
    return parseFloat(cs.columnGap || cs.gap || '') || fallback || 0;
  }

  var MOBILE = window.matchMedia('(max-width: 767px)');

  function onMedia(mql, fn) {
    if (mql.addEventListener) mql.addEventListener('change', fn);
    else if (mql.addListener) mql.addListener(fn);
  }

  function reduceMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ── Prateleira de produtos (product-shelf.phtml) ── */
  function initShelf(scroller) {
    if (!once(scroller, 'vvShelf')) return;
    var wrapper = scroller.closest('.vv-shelf-carousel-wrap');
    if (!wrapper) return;
    var id = scroller.id;
    var prev = wrapper.querySelector('.vv-scroll-prev[data-vv-scroll-target="' + id + '"]');
    var next = wrapper.querySelector('.vv-scroll-next[data-vv-scroll-target="' + id + '"]');
    var dotsWrap = document.getElementById(id + '-dots');
    var dots = dotsWrap ? dotsWrap.querySelectorAll('.vv-dot') : [];
    var groupDots = Array.prototype.slice.call(wrapper.querySelectorAll('[data-vv-group]'));
    var cards = Array.prototype.slice.call(scroller.children).filter(function (c) {
      return c.classList.contains('vv-product-card');
    });
    var groups = Math.max(1, Math.ceil(cards.length / 2));
    var index = 0;
    var cw = null;

    /* Desktop: como na produção (setas andam 2 cards; pontos proporcionais à rolagem) */
    function cardWidth() {
      if (cw) return cw;
      var c = scroller.querySelector('.vv-product-card');
      if (!c) return scroller.clientWidth;
      cw = c.offsetWidth + gapOf(scroller, 14);
      return cw;
    }

    var raf;
    function updateNav() {
      if (MOBILE.matches || raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var max = scroller.scrollWidth - scroller.clientWidth - 2;
        if (prev) prev.classList.toggle('is-disabled', scroller.scrollLeft <= 2);
        if (next) next.classList.toggle('is-disabled', scroller.scrollLeft >= max);
        if (dots.length > 0) {
          var maxScroll = scroller.scrollWidth - scroller.clientWidth;
          var page = maxScroll > 0 ? Math.round((scroller.scrollLeft / maxScroll) * (dots.length - 1)) : 0;
          for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('active', i === page);
        }
      });
    }

    /* Celular: grupos de dois cards inteiros (página = largura útil + o gap) */
    function pageWidth() {
      return scroller.clientWidth + gapOf(scroller, 12);
    }

    function paintGroups() {
      cards.forEach(function (c, i) { c.inert = Math.floor(i / 2) !== index; });
      groupDots.forEach(function (d, i) { d.setAttribute('aria-disabled', i === index ? 'true' : 'false'); });
      if (prev) { prev.classList.remove('is-disabled'); prev.disabled = index <= 0; }
      if (next) { next.classList.remove('is-disabled'); next.disabled = index >= groups - 1; }
    }

    function goTo(n, instant) {
      index = Math.max(0, Math.min(groups - 1, n));
      paintGroups();
      scroller.scrollTo({ left: index * pageWidth(), behavior: instant || reduceMotion() ? 'instant' : 'smooth' });
    }

    var settle;
    function syncFromScroll() {
      clearTimeout(settle);
      settle = setTimeout(function () {
        var w = pageWidth();
        if (w <= 0) return;
        var n = Math.max(0, Math.min(groups - 1, Math.round(scroller.scrollLeft / w)));
        if (n !== index) { index = n; paintGroups(); }
      }, 120);
    }

    function leaveMobile() {
      cards.forEach(function (c) { c.inert = false; });
      if (prev) prev.disabled = false;
      if (next) next.disabled = false;
      updateNav();
    }

    if (prev) {
      prev.addEventListener('click', function () {
        if (MOBILE.matches) { goTo(index - 1); return; }
        cw = null;
        scroller.scrollBy({ left: -cardWidth() * 2, behavior: 'smooth' });
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        if (MOBILE.matches) { goTo(index + 1); return; }
        cw = null;
        scroller.scrollBy({ left: cardWidth() * 2, behavior: 'smooth' });
      });
    }
    groupDots.forEach(function (d, i) {
      d.addEventListener('click', function () { goTo(i); });
    });
    scroller.addEventListener('scroll', function () {
      if (MOBILE.matches) syncFromScroll();
      else updateNav();
    }, { passive: true });

    var resizeTimer;
    window.addEventListener('resize', function () {
      cw = null;
      if (!MOBILE.matches) return;
      clearTimeout(resizeTimer);
      // Rotação/zoom: mantém o grupo atual com a largura nova
      resizeTimer = setTimeout(function () { goTo(index, true); }, 150);
    }, { passive: true });
    onMedia(MOBILE, function () {
      if (MOBILE.matches) goTo(index, true);
      else leaveMobile();
    });

    if (MOBILE.matches) {
      index = Math.max(0, Math.min(groups - 1, Math.round(scroller.scrollLeft / (pageWidth() || 1))));
      paintGroups();
    } else {
      updateNav();
    }
    initClickDrag(scroller);
  }

  /* ── Carrossel paginado (produtores, harmonizações, Instagram) ── */
  function initPageCarousel(carousel) {
    if (!once(carousel, 'vvPage')) return;
    var id = carousel.id;
    var dotsWrap = document.getElementById(carousel.getAttribute('data-vv-dots') || '');
    var wrap = carousel.closest('.vv-page-carousel-wrap') || carousel.parentElement;
    var prev = wrap ? wrap.querySelector('.vv-scroll-prev[data-vv-scroll-target="' + id + '"]') : null;
    var next = wrap ? wrap.querySelector('.vv-scroll-next[data-vv-scroll-target="' + id + '"]') : null;

    function itemsPerPage() {
      var first = carousel.firstElementChild;
      if (!first) return 1;
      var iw = first.getBoundingClientRect().width;
      if (iw <= 0) return 1;
      var gap = gapOf(carousel, 0);
      return Math.max(1, Math.round((carousel.clientWidth + gap) / (iw + gap)));
    }

    function pageCount() {
      return Math.max(1, Math.ceil(carousel.children.length / itemsPerPage()));
    }

    function currentPage() {
      var max = carousel.scrollWidth - carousel.clientWidth;
      if (max <= 0) return 0;
      return Math.round((carousel.scrollLeft / max) * (pageCount() - 1));
    }

    function renderDots() {
      if (!dotsWrap) return;
      var pages = pageCount();
      if (dotsWrap.children.length !== pages) {
        dotsWrap.innerHTML = '';
        for (var i = 0; i < pages; i++) {
          var dot = document.createElement('span');
          dot.className = 'vv-dot' + (i === 0 ? ' active' : '');
          dot.setAttribute('data-page', String(i));
          dotsWrap.appendChild(dot);
        }
      }
      dotsWrap.classList.toggle('vv-dots-single', pages <= 1);
    }

    function scrollToPage(p) {
      var pages = pageCount();
      if (pages <= 1) return;
      var max = carousel.scrollWidth - carousel.clientWidth;
      carousel.scrollTo({ left: max * (p / (pages - 1)), behavior: 'smooth' });
    }

    var raf;
    function syncDots() {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        if (prev) prev.classList.toggle('is-disabled', carousel.scrollLeft <= 2);
        var max = carousel.scrollWidth - carousel.clientWidth - 2;
        if (next) next.classList.toggle('is-disabled', carousel.scrollLeft >= max);
        if (!dotsWrap) return;
        var dots = dotsWrap.querySelectorAll('.vv-dot');
        var page = currentPage();
        for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('active', i === page);
      });
    }

    if (prev) prev.addEventListener('click', function () { scrollToPage(Math.max(0, currentPage() - 1)); });
    if (next) next.addEventListener('click', function () { scrollToPage(Math.min(pageCount() - 1, currentPage() + 1)); });
    if (dotsWrap) {
      dotsWrap.addEventListener('click', function (e) {
        var t = e.target.closest('.vv-dot');
        if (!t) return;
        scrollToPage(parseInt(t.getAttribute('data-page') || '0', 10));
      });
    }
    carousel.addEventListener('scroll', syncDots, { passive: true });
    window.addEventListener('resize', function () { renderDots(); syncDots(); }, { passive: true });
    renderDots();
    syncDots();
    initClickDrag(carousel);
  }

  /* ── Curadoria (scroll-snap com pontos já renderizados) ── */
  function initSnapCarousel(carousel) {
    if (!once(carousel, 'vvSnap')) return;
    var dotsWrap = document.getElementById(carousel.getAttribute('data-vv-dots') || '');
    initClickDrag(carousel);
    if (!dotsWrap) return;
    var dots = dotsWrap.querySelectorAll('.vv-dot');
    if (!dots.length) return;
    var raf;
    function updateDots() {
      if (raf) return;
      raf = requestAnimationFrame(function () {
        raf = null;
        var max = carousel.scrollWidth - carousel.clientWidth;
        var page = max > 0 ? Math.round((carousel.scrollLeft / max) * (dots.length - 1)) : 0;
        for (var i = 0; i < dots.length; i++) dots[i].classList.toggle('active', i === page);
      });
    }
    carousel.addEventListener('scroll', updateDots, { passive: true });
    updateDots();
  }

  /* ── Arrastar com o mouse (só ponteiro fino; o clique sem arrasto continua navegando) ── */
  function initClickDrag(el) {
    if (!el || !once(el, 'vvDragInit')) return;
    var isDown = false;
    var didDrag = false;
    var startX = 0;
    var startScroll = 0;
    var threshold = 6;

    el.addEventListener('mousedown', function (e) {
      if (e.button !== 0) return;
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
      if (e.target.closest('button, input, select, textarea, form')) return;
      isDown = true;
      didDrag = false;
      startX = e.pageX;
      startScroll = el.scrollLeft;
    });

    function endDrag() {
      if (!isDown) return;
      isDown = false;
      el.classList.remove('vv-dragging');
    }

    window.addEventListener('mouseup', endDrag);
    el.addEventListener('mouseleave', endDrag);
    el.addEventListener('mousemove', function (e) {
      if (!isDown) return;
      var dx = e.pageX - startX;
      if (Math.abs(dx) > threshold) {
        if (!didDrag) {
          didDrag = true;
          el.classList.add('vv-dragging');
        }
        e.preventDefault();
        el.scrollLeft = startScroll - dx;
      }
    });
    el.addEventListener('click', function (e) {
      if (didDrag) {
        e.preventDefault();
        e.stopPropagation();
      }
      didDrag = false;
    }, true);
    el.addEventListener('dragstart', function (e) {
      if (isDown) e.preventDefault();
    });
  }

  /* ── Hero: crossfade com pontos (mesmo rotador da produção) ── */
  function initHero(el) {
    if (!once(el, 'vvHero')) return;
    var slides = Array.prototype.slice.call(el.querySelectorAll(':scope > .vv-hero-slide'));
    if (slides.length < 2) return;
    var interval = (parseInt(el.getAttribute('data-vv-interval'), 10) || 5) * 1000;
    el.classList.add('vv-hero-rotating');
    slides.forEach(function (s, i) {
      s.classList.toggle('is-active', i === 0);
      s.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
      var link = s.matches('a') ? s : s.querySelector('a');
      if (link) link.tabIndex = i === 0 ? 0 : -1;
    });

    var cur = 0;
    var labelDot = el.getAttribute('data-vv-label-dot') || 'Mostrar banner [n] de [total]';
    // Controles: no celular "‹ ● ○ ○ ○ ○ ›" (layout de 05/10/2026); no desktop só os pontos (as setas
    // e o invólucro somem no CSS), como antes
    var controls = document.createElement('div');
    controls.className = 'vv-hero-controls';
    function arrow(cls, label, glyph, step) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vv-hero-arrow ' + cls;
      b.setAttribute('aria-label', label);
      b.innerHTML = '<span aria-hidden="true">' + glyph + '</span>';
      b.addEventListener('click', function () { go(cur + step, true); });
      return b;
    }
    var dotsWrap = document.createElement('div');
    dotsWrap.className = 'vv-hero-dots';
    var dotEls = slides.map(function (s, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vv-hero-dot' + (i === 0 ? ' is-active' : '');
      b.setAttribute('aria-label', labelDot.replace('[n]', i + 1).replace('[total]', slides.length));
      b.setAttribute('aria-disabled', i === 0 ? 'true' : 'false');
      b.addEventListener('click', function () { go(i, true); });
      dotsWrap.appendChild(b);
      return b;
    });
    controls.appendChild(arrow('vv-hero-prev', el.getAttribute('data-vv-label-prev') || 'Banner anterior', '&lsaquo;', -1));
    controls.appendChild(dotsWrap);
    controls.appendChild(arrow('vv-hero-next', el.getAttribute('data-vv-label-next') || 'Próximo banner', '&rsaquo;', 1));
    el.parentNode.insertBefore(controls, el.nextSibling);

    // Rotação automática como na produção, pausada com o ponteiro ou o foco no banner e desligada
    // para quem pede movimento reduzido
    var paused = false;
    var timer = reduceMotion() ? null : setInterval(tick, interval);
    function tick() {
      if (!el.isConnected) { clearInterval(timer); return; }
      if (document.hidden || paused) return;
      go(cur + 1, false);
    }
    [el, controls].forEach(function (node) {
      node.addEventListener('mouseenter', function () { paused = true; });
      node.addEventListener('mouseleave', function () { paused = false; });
      node.addEventListener('focusin', function () { paused = true; });
      node.addEventListener('focusout', function () { paused = false; });
    });
    function go(n, manual) {
      var prev = cur;
      cur = ((n % slides.length) + slides.length) % slides.length;
      if (prev === cur) return;
      slides[prev].classList.remove('is-active');
      slides[prev].setAttribute('aria-hidden', 'true');
      slides[cur].classList.add('is-active');
      slides[cur].setAttribute('aria-hidden', 'false');
      var pl = slides[prev].matches('a') ? slides[prev] : slides[prev].querySelector('a');
      var cl = slides[cur].matches('a') ? slides[cur] : slides[cur].querySelector('a');
      if (pl) pl.tabIndex = -1;
      if (cl) cl.tabIndex = 0;
      dotEls[prev].classList.remove('is-active');
      dotEls[prev].setAttribute('aria-disabled', 'false');
      dotEls[cur].classList.add('is-active');
      dotEls[cur].setAttribute('aria-disabled', 'true');
      if (manual && timer) {
        clearInterval(timer);
        timer = setInterval(tick, interval);
      }
    }

    // Arrastar/deslizar troca o banner (o owl da produção também aceita swipe). Ajuste 05 de 08/10/2026:
    // o gesto horizontal é do banner (touch-action: pan-y no CSS, sem o navegador cancelar o arrasto),
    // com trava de direção — rolar a página na vertical não troca o banner — e limiar curto: 12% da
    // largura (entre 30 e 60px) ou um peteleco rápido.
    var drag = null;
    var swiped = false;
    el.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      drag = { x: e.clientX, y: e.clientY, t: Date.now(), axis: null };
      swiped = false;
    });
    el.addEventListener('pointermove', function (e) {
      if (!drag || drag.axis) return;
      var ax = Math.abs(e.clientX - drag.x);
      var ay = Math.abs(e.clientY - drag.y);
      if (ax > 8 || ay > 8) drag.axis = ax > ay ? 'x' : 'y';
    });
    el.addEventListener('pointerup', function (e) {
      if (!drag) return;
      var d = drag;
      drag = null;
      if (d.axis === 'y') return;
      var dx = e.clientX - d.x;
      var dist = Math.abs(dx);
      var far = dist > Math.min(60, Math.max(30, el.clientWidth * 0.12));
      var flick = dist > 20 && dist / Math.max(1, Date.now() - d.t) > 0.35;
      if (far || flick) {
        swiped = true;
        go(cur + (dx < 0 ? 1 : -1), true);
      }
    });
    el.addEventListener('pointercancel', function () { drag = null; });
    el.addEventListener('click', function (e) {
      if (!swiped) return;
      e.preventDefault();
      swiped = false;
    }, true);
    el.addEventListener('dragstart', function (e) { e.preventDefault(); });
  }

  /* ── Página do produto ── */
  function initQty(scope) {
    scope.querySelectorAll('.vv-pp-qty').forEach(function (box) {
      if (!once(box, 'vvQty')) return;
      box.addEventListener('click', function (e) {
        var btn = e.target.closest('.vv-qty-btn');
        if (!btn) return;
        var input = box.querySelector('.vv-qty-input');
        if (!input) return;
        var v = parseInt(input.value, 10) || 1;
        v += btn.getAttribute('data-act') === '+' ? 1 : -1;
        if (v < 1) v = 1;
        input.value = v;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  function mainBuyForm() {
    return document.querySelector('.vv-product-page .vv-pp-buy-form');
  }

  function setMainQty(qty) {
    var form = mainBuyForm();
    var input = form && form.querySelector('.vv-qty-input');
    if (input) {
      input.value = qty;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    return form;
  }

  function initPriceOptions(scope) {
    scope.querySelectorAll('.vv-price-option[data-vv-qty]').forEach(function (opt) {
      if (!once(opt, 'vvOpt')) return;
      function select(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        document.querySelectorAll('.vv-price-option[data-vv-qty]').forEach(function (o) {
          o.classList.toggle('active', o === opt);
        });
        setMainQty(parseInt(opt.getAttribute('data-vv-qty'), 10) || 1);
      }
      opt.addEventListener('click', select);
      opt.addEventListener('keydown', select);
    });
  }

  function initTiers(scope) {
    scope.querySelectorAll('.vv-pp-progressive-item[data-vv-tier-qty]').forEach(function (li) {
      if (!once(li, 'vvTier')) return;
      var qty = parseInt(li.getAttribute('data-vv-tier-qty'), 10) || 1;
      function add(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        var form = setMainQty(qty);
        if (!form) return;
        var btn = form.querySelector('button[type="submit"]');
        if (btn) btn.click();
        else if (form.requestSubmit) form.requestSubmit();
      }
      li.addEventListener('click', add);
      li.addEventListener('keydown', add);
    });
  }

  function initCollapsibles(scope) {
    var more = (window.VvMagentoStrings && window.VvMagentoStrings.more) || '+ ver mais';
    var less = (window.VvMagentoStrings && window.VvMagentoStrings.less) || '- ver menos';
    scope.querySelectorAll('[data-vv-collapsible]').forEach(function (blk) {
      if (!once(blk, 'vvCollapsible')) return;
      var content = blk.querySelector('[data-vv-collapsible-content]');
      var btn = blk.querySelector('[data-vv-collapsible-toggle]');
      if (!content || !btn) return;
      if (content.scrollHeight <= 110) {
        btn.style.display = 'none';
        content.classList.remove('is-collapsed');
        return;
      }
      btn.setAttribute('aria-expanded', 'false');
      btn.addEventListener('click', function () {
        var expanded = !content.classList.contains('is-collapsed');
        content.classList.toggle('is-collapsed', expanded);
        btn.textContent = expanded ? more : less;
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      });
    });
  }

  /* ── Barra fixa (R01): preço + "Adicionar"; aparece quando o bloco de compra sai da tela ── */
  function initStickyBuy(scope) {
    var bar = scope.querySelector('[data-vv-sticky]');
    if (!bar || !once(bar, 'vvSticky')) return;
    var form = mainBuyForm();
    // Produto vendido fora do carrinho: o "botão principal" é o link Comprar (vv-m-cart-form)
    var mainBtn = form && form.querySelector('button[type="submit"], [data-vv-buy-link]');
    var stickyBtn = bar.querySelector('[data-vv-sticky-add]');
    if (!form || !mainBtn || !stickyBtn) return;
    var mq = window.matchMedia('(max-width: 991px)');
    var rootStyle = document.documentElement.style;
    var navToggle = document.getElementById('vv-nav-toggle');
    var mainVisible = true;
    var measured = false;

    // Gavetas e avisos que ocupam o mesmo espaço: carrinho lateral, menu, diálogos e o banner de
    // consentimento de cookies do Shopify
    function blocked() {
      if (document.body.classList.contains('vv-sidecart-open')) return true;
      if (navToggle && navToggle.checked) return true;
      if (document.querySelector('dialog[open]')) return true;
      var consent = document.getElementById('shopify-pc__banner');
      if (consent && consent.getClientRects().length && getComputedStyle(consent).visibility !== 'hidden') return true;
      return false;
    }

    function measure() {
      rootStyle.setProperty('--vv-sticky-h', bar.hidden ? '0px' : Math.ceil(bar.getBoundingClientRect().height) + 'px');
    }

    function update() {
      // Fica oculta até a primeira medição da posição do bloco de compra
      var show = measured && mq.matches && !mainVisible && !blocked();
      if (bar.hidden === !show) return;
      bar.hidden = !show;
      document.body.classList.toggle('vv-has-sticky-buy', show);
      measure();
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        mainVisible = entries[entries.length - 1].isIntersecting;
        measured = true;
        update();
      }, { threshold: 0 }).observe(form);
    } else {
      measured = true;
      window.addEventListener('scroll', function () {
        var r = form.getBoundingClientRect();
        mainVisible = r.bottom > 0 && r.top < window.innerHeight;
        update();
      }, { passive: true });
    }
    if ('ResizeObserver' in window) new ResizeObserver(measure).observe(bar);
    onMedia(mq, update);
    if (navToggle) navToggle.addEventListener('change', update);
    new MutationObserver(update).observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
    document.addEventListener('visibilitychange', update);
    setTimeout(update, 1500);

    // Mesmo item, quantidade, validação, carregamento e erros: a barra só aciona o botão principal
    stickyBtn.addEventListener('click', function () {
      if (mainBtn.disabled || mainBtn.getAttribute('aria-busy') === 'true') return;
      mainBtn.click();
    });
    function mirror() {
      stickyBtn.disabled = mainBtn.disabled;
      ['vv-btn-loading', 'vv-btn-added'].forEach(function (cls) {
        stickyBtn.classList.toggle(cls, mainBtn.classList.contains(cls));
      });
      if (mainBtn.hasAttribute('aria-busy')) stickyBtn.setAttribute('aria-busy', mainBtn.getAttribute('aria-busy'));
      else stickyBtn.removeAttribute('aria-busy');
    }
    new MutationObserver(mirror).observe(mainBtn, { attributes: true, attributeFilter: ['class', 'disabled', 'aria-busy'] });
    mirror();
  }

  /* ── Acordeões das descrições: fechados no celular, abertos (sem resumo) no desktop ── */
  function initAccordions(scope) {
    var accs = Array.prototype.slice.call(scope.querySelectorAll('[data-vv-acc]')).filter(function (d) {
      return once(d, 'vvAcc');
    });
    if (!accs.length) return;
    function apply() {
      accs.forEach(function (d) {
        if (d.hasAttribute('data-vv-acc-closed')) return;
        d.open = !MOBILE.matches;
      });
    }
    onMedia(MOBILE, apply);
    apply();
  }

  /* ── Valores em dinheiro no formato da loja (para atualizar preços sem recarregar a página) ── */
  function formatMoney(cents, format) {
    cents = Math.round(Number(cents) || 0);
    function group(n, decimals, thousands, dec) {
      var fixed = (n / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return decimals ? whole + dec + parts[1] : whole;
    }
    format = format || 'R$ {{amount_with_comma_separator}}';
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (m, key) {
      switch (key) {
        case 'amount': return group(cents, 2, ',', '.');
        case 'amount_no_decimals': return group(cents, 0, ',', '.');
        case 'amount_no_decimals_with_comma_separator': return group(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return group(cents, 2, "'", '.');
        default: return group(cents, 2, '.', ',');
      }
    });
  }

  /* ── Campanha (Flash Sale): cronômetro pelo relógio do servidor; ao terminar, confirma o preço ── */
  function initCampaign(scope) {
    var el = scope.querySelector('[data-vv-campaign]');
    if (!el || !once(el, 'vvCampaign')) return;
    var end = (parseInt(el.getAttribute('data-vv-end'), 10) || 0) * 1000;
    var clock = el.querySelector('[data-vv-clock]');
    if (!end) return;
    var units = {};
    if (clock) {
      clock.querySelectorAll('[data-u]').forEach(function (u) {
        (units[u.getAttribute('data-u')] = units[u.getAttribute('data-u')] || []).push(u);
      });
    }
    var offset = 0;
    var timer = null;
    var expired = false;

    function now() { return Date.now() + offset; }

    // Hora do servidor pelo cabeçalho Date de uma resposta sem cache (resolução de 1 s)
    function syncClock() {
      var t0 = Date.now();
      return fetch(rootUrl() + 'cart.js', { cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json' } })
        .then(function (r) {
          var server = Date.parse(r.headers.get('Date') || '');
          if (!isNaN(server)) offset = server + 500 - (t0 + Date.now()) / 2;
        })
        .catch(function () {});
    }

    function pad(n) { return (n < 10 ? '0' : '') + n; }
    function set(key, value) {
      var u = units[key];
      if (u && u[0]) u[0].querySelector('b').textContent = pad(value);
    }

    function render() {
      var left = Math.floor((end - now()) / 1000);
      if (left <= 0) { expire(); return; }
      if (!clock) return;
      var d = Math.floor(left / 86400);
      (units.d || []).forEach(function (u) { u.hidden = d === 0; });
      clock.classList.toggle('has-days', d > 0);
      set('d', d);
      set('h', Math.floor((left % 86400) / 3600));
      set('m', Math.floor((left % 3600) / 60));
      set('s', left % 60);
    }

    function start() {
      clearInterval(timer);
      render();
      if (!expired) timer = setInterval(render, 1000);
    }

    function expire() {
      if (expired) return;
      expired = true;
      clearInterval(timer);
      el.hidden = true;
      refreshProduct(el.getAttribute('data-vv-handle'), el.getAttribute('data-vv-variant'));
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden && !expired) syncClock().then(start);
    });
    start();
    syncClock().then(start);
  }

  // Fim da campanha: os botões esperam a confirmação de preço e estoque da loja (nada de promoção vencida)
  function refreshProduct(handle, variantId) {
    var page = document.querySelector('.vv-product-page');
    var form = mainBuyForm();
    var mainBtn = form && form.querySelector('button[type="submit"]');
    if (!page || !handle) return;
    if (mainBtn) mainBtn.disabled = true;
    fetch(rootUrl() + 'products/' + encodeURIComponent(handle) + '.js?_=' + Date.now(), { cache: 'no-store', credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r.status); })
      .then(function (p) {
        var v = (p.variants || []).filter(function (x) { return String(x.id) === String(variantId); })[0] || (p.variants || [])[0];
        if (!v) return;
        var fmt = page.getAttribute('data-vv-money-format');
        var price = formatMoney(v.price, fmt);
        var hasCompare = v.compare_at_price && v.compare_at_price > v.price;
        page.querySelectorAll('[data-vv-price]').forEach(function (n) { n.textContent = price; });
        document.querySelectorAll('[data-vv-sticky-price]').forEach(function (n) { n.textContent = price; });
        page.querySelectorAll('[data-vv-compare]').forEach(function (n) {
          n.hidden = !hasCompare;
          var amount = n.querySelector('[data-vv-compare-amount]');
          if (amount) amount.textContent = hasCompare ? formatMoney(v.compare_at_price, fmt) : '';
        });
        page.querySelectorAll('[data-vv-installments]').forEach(function (n) {
          var start = Number(n.getAttribute('data-start')) || 0;
          var step = Number(n.getAttribute('data-step')) || 0;
          var max = Number(n.getAttribute('data-max')) || 1;
          var k = v.price >= start && max > 1 && step > 0 ? Math.min(max, 2 + Math.floor((v.price - start) / step)) : 1;
          var tpl = n.getAttribute(k > 1 ? 'data-tpl-many' : 'data-tpl-one');
          var out = n.querySelector('[data-vv-installments-text]');
          if (out && tpl) out.innerHTML = tpl.replace('[n]', k).replace('[amount]', formatMoney(Math.round(v.price / k), fmt));
        });
        page.querySelectorAll('[data-vv-pix]').forEach(function (n) {
          var pct = Number(n.getAttribute('data-pct')) || 0;
          var tpl = n.getAttribute('data-tpl');
          var out = n.querySelector('[data-vv-pix-text]');
          if (out && tpl) out.innerHTML = tpl.replace('[amount]', formatMoney(Math.round(v.price * (100 - pct) / 100), fmt));
        });
        // "Economize R$ X por garrafa" só existe durante a campanha (rev5): encerrada, a linha sai
        page.querySelectorAll('[data-vv-saving]').forEach(function (n) {
          n.hidden = true;
          n.textContent = '';
        });
        page.querySelectorAll('[data-vv-tier-pct]').forEach(function (li) {
          var pct = parseFloat(li.getAttribute('data-vv-tier-pct')) || 0;
          var out = li.querySelector('[data-vv-tier-price]');
          if (out) out.textContent = formatMoney(Math.floor(((100 - pct) * v.price) / 100), fmt);
        });
        if (!mainBtn) return;
        if (v.available) {
          mainBtn.disabled = false;
        } else {
          mainBtn.disabled = true;
          var label = page.getAttribute('data-vv-unavailable');
          if (label) mainBtn.textContent = label;
        }
      })
      .catch(function () {
        // Sem confirmação o botão segue desativado; a mensagem pede para recarregar
        var err = form && form.querySelector('[data-vv-cart-error]');
        if (err) {
          err.lastChild.textContent = ' ' + (window.VvMagentoStrings && window.VvMagentoStrings.reload || 'A oferta terminou. Atualize a página para ver o preço atual.');
          err.classList.remove('hidden');
        }
      });
  }

  /* ── Estados do botão como na produção: .vv-btn-loading (spinner) → .vv-btn-added ("✓ Adicionado!") ──
     Um envio por vez por formulário: enquanto o carrinho não responde, novos cliques e envios do mesmo
     formulário (botão principal, barra fixa, faixas do progressivo, Enter na quantidade) são ignorados.
     "Adicionado!" só quando o carrinho confirma sem erro (o Horizon resolve a promessa com didError). */
  function busyGuard(e) {
    var host = e.target && e.target.closest ? e.target.closest('.vv-m-form') : null;
    if (!host || host.getAttribute('data-vv-busy') !== '1') return;
    if (e.type === 'click' && !e.target.closest('button[type="submit"]')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
  }
  document.addEventListener('click', busyGuard, true);
  document.addEventListener('submit', busyGuard, true);

  document.addEventListener('shopify:cart:lines-update', function (e) {
    var host = e.target && e.target.closest ? e.target.closest('.vv-m-form') : null;
    if (!host) return;
    var btn = host.querySelector('button[type="submit"]');
    if (!btn) return;
    host.setAttribute('data-vv-busy', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.classList.remove('vv-btn-added');
    btn.classList.add('vv-btn-loading');
    var finished = false;
    function done(ok) {
      if (finished) return;
      finished = true;
      host.removeAttribute('data-vv-busy');
      btn.removeAttribute('aria-busy');
      btn.classList.remove('vv-btn-loading');
      if (!ok) return;
      btn.classList.add('vv-btn-added');
      setTimeout(function () { btn.classList.remove('vv-btn-added'); }, 1600);
    }
    // Rede travada: libera o botão para uma nova tentativa
    setTimeout(function () { done(false); }, 20000);
    Promise.resolve(e.promise).then(function (result) {
      done(!(result && result.detail && result.detail.didError));
    }, function () {
      done(false);
      var err = host.querySelector('[data-vv-cart-error]');
      if (err && err.classList.contains('hidden')) {
        err.lastChild.textContent = ' ' + (window.VvMagentoStrings && window.VvMagentoStrings.cartError || 'Não foi possível adicionar ao carrinho. Tente novamente.');
        err.classList.remove('hidden');
        setTimeout(function () { err.classList.add('hidden'); }, 10000);
      }
    });
  });
  document.addEventListener('shopify:cart:error', function (e) {
    var host = e.target && e.target.closest ? e.target.closest('.vv-m-form') : null;
    var btn = host && host.querySelector('button[type="submit"]');
    if (btn) btn.classList.remove('vv-btn-loading');
  });

  /* ── "Você também pode gostar": recomendações do Shopify ── */
  if (!customElements.get('vv-recommendations')) {
    customElements.define('vv-recommendations', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded) return;
        this.dataset.loaded = '1';
        var self = this;
        var url = this.getAttribute('data-url');
        if (!url) return;
        fetch(url)
          .then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (text) {
            if (!text) return;
            var doc = new DOMParser().parseFromString(text, 'text/html');
            var shelf = doc.querySelector('.vv-shelf-wrapper');
            if (!shelf) { self.remove(); return; }
            self.replaceWith(shelf);
            init(shelf.parentElement || document);
          })
          .catch(function () {});
      }
    });
  }

  /* ── Cabeçalho (header.phtml): acordeão do menu no celular ── */
  function initHeaderNav(scope) {
    var nav = scope.querySelector('.vv-nav');
    if (!nav || !once(nav, 'vvNav')) return;
    nav.addEventListener('click', function (e) {
      if (window.innerWidth >= 992) return;
      var anchor = e.target.closest('a');
      if (!anchor) return;
      var li = anchor.parentElement;
      if (!li || !li.classList.contains('vv-has-children')) return;
      e.preventDefault();
      e.stopPropagation();
      var wasOpen = li.classList.contains('vv-open');
      nav.querySelectorAll('.vv-has-children.vv-open').forEach(function (s) {
        if (s !== li) s.classList.remove('vv-open');
      });
      li.classList.toggle('vv-open', !wasOpen);
      if (typeof anchor.blur === 'function') anchor.blur();
      if (!wasOpen) {
        var inner = nav.querySelector('.vv-nav-inner');
        if (inner) {
          requestAnimationFrame(function () {
            if (li.getBoundingClientRect().top < inner.getBoundingClientRect().top + 10) {
              li.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          });
        }
      }
    }, true);
    var toggle = document.getElementById('vv-nav-toggle');
    if (toggle) {
      toggle.addEventListener('change', function () {
        if (!toggle.checked) {
          nav.querySelectorAll('.vv-has-children.vv-open').forEach(function (s) { s.classList.remove('vv-open'); });
        }
      });
    }
  }

  /* iOS: wa.me às vezes substitui a aba da loja; abre explicitamente em outra aba */
  function initWhatsAppSafeOpen(scope) {
    scope.querySelectorAll('a[data-vv-whatsapp="1"]').forEach(function (a) {
      if (!once(a, 'vvWa')) return;
      a.addEventListener('click', function (e) {
        var href = a.getAttribute('href');
        if (!href) return;
        e.preventDefault();
        var w = window.open(href, '_blank', 'noopener,noreferrer');
        if (!w) window.location.href = href;
      });
    });
  }

  /* Contador do carrinho do cabeçalho (o clique no ícone abre o carrinho lateral — vv-sidecart.js) */
  function setCartCount(count) {
    document.querySelectorAll('[data-vv-cart-counter]').forEach(function (el) {
      el.classList.toggle('empty', !count);
      var n = el.querySelector('.counter-number');
      if (n) n.textContent = count;
    });
  }

  var countTimer;
  function refreshCartCount() {
    clearTimeout(countTimer);
    countTimer = setTimeout(function () {
      var rootUrl = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(rootUrl + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (cart) { if (cart) setCartCount(cart.item_count); })
        .catch(function () {});
    }, 250);
  }
  ['shopify:cart:lines-update', 'cart:update'].forEach(function (name) {
    document.addEventListener(name, function (e) {
      if (e.promise && typeof e.promise.then === 'function') e.promise.then(refreshCartCount, refreshCartCount);
      else refreshCartCount();
    });
  });
  document.addEventListener('vv:cart:count', function (e) {
    if (e.detail && typeof e.detail.count === 'number') setCartCount(e.detail.count);
  });
  window.addEventListener('pageshow', function (e) { if (e.persisted) refreshCartCount(); });

  /* ── Boot ── */
  function init(root) {
    root = root || document;
    root.querySelectorAll('.vv-shelf-carousel-wrap .vv-product-scroller').forEach(initShelf);
    root.querySelectorAll('[data-vv-page-carousel]').forEach(initPageCarousel);
    root.querySelectorAll('[data-vv-snap-carousel]').forEach(initSnapCarousel);
    root.querySelectorAll('[data-vv-hero]').forEach(initHero);
    initQty(root);
    initPriceOptions(root);
    initTiers(root);
    initAccordions(root);
    initCollapsibles(root);
    initStickyBuy(root.querySelector ? root : document);
    initCampaign(root.querySelector ? root : document);
    initHeaderNav(root);
    initWhatsAppSafeOpen(root);
  }

  window.VvMagento = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
